import { useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import useTitanApp from "@/hooks/useTitanApp";
import {
  setShowIdlePopup,
  removeAuth,
  setAuth,
} from "@/dispatchers/appDispatchers";
import { AlertTriangle, LucideClockAlert, X } from "lucide-react";
import { Button } from "../ui/button";
import { useTimer } from "react-timer-hook";
import useIdleTimeout from "@/hooks/useIdleTimeout";
import { logErrorWithPayload } from "@/services/logService";
import {
  showInfo,
  showError,
} from "@/components/ToastedMessage/ToastedMessage";

export default function IdleWarning() {
  const { appState, signout } = useTitanApp();
  const auth = useAuth0();
  const { loginWithRedirect, getAccessTokenSilently, logout, user } =
    auth || {};
  const { darkMode } = appState;

  const { idleTimer } = useIdleTimeout();

  const expiryTime = new Date();
  expiryTime.setSeconds(expiryTime.getSeconds() + 120);

  const { seconds, minutes, restart } = useTimer({
    expiryTimestamp: expiryTime,
    autoStart: true,
    onExpire: () => {
      signout();
    },
  });

  const handleClose = () => {
    setShowIdlePopup(false);
  };

  const handleContinue = async () => {
    idleTimer.reset();
    localStorage.setItem("session-continue", Date.now().toString());
    restart(
      new Date(new Date().setSeconds(new Date().getSeconds() + 120)),
      true
    );

    let newAuthToken = "";

    try {
      newAuthToken = await getAccessTokenSilently();
    } catch (error) {
      if (error?.error === "login_required") {
        localStorage.setItem("loginRequired", "true");
        removeAuth();
        setTimeout(
          (loginWithRedirect) => {
            loginWithRedirect();
          },
          100,
          loginWithRedirect
        );
      } else {
        logErrorWithPayload(
          error?.error || "You are unauthorized or blocked..."
        );
        showError(error?.error || "You are unauthorized or blocked...");
      }
    }

    setAuth(newAuthToken);
    setShowIdlePopup(false);
  };

  useEffect(() => {
    restart(expiryTime, true);
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm bg-black/40"
      onClick={handleClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`
          relative w-full max-w-lg p-8 rounded-2xl shadow-2xl
          animate-[fadeIn_0.25s_ease-out,scaleIn_0.25s_ease-out]
          ${darkMode ? "bg-gray-900 text-white" : "bg-white text-gray-900"}
        `}
      >
        <button
          onClick={handleClose}
          className={`absolute top-4 right-4 p-2 rounded-full transition ${
            darkMode
              ? "text-gray-300 hover:bg-gray-700"
              : "text-gray-500 hover:bg-gray-200"
          }`}
        >
          <X size={18} />
        </button>

        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/30">
              <AlertTriangle className="w-6 h-6 text-amber-600 dark:text-amber-500" />
            </div>
            <p className="text-xl font-semibold leading-none tracking-tight">
              Session Timeout Warning
            </p>
          </div>

          <p className="pt-2 text-base tracking-tight text-muted-foreground">
            Your session is about to expire due to inactivity. Choose to
            continue working or logout.
          </p>
          <div className="py-6">
            <div className="text-center">
              <p className="mb-3 text-sm text-gray-600 dark:text-gray-400">
                Time remaining:
              </p>
              <div className="mb-3 font-mono text-5xl font-bold text-amber-600 dark:text-amber-500">
                {minutes}:{seconds.toString().padStart(2, "0")}
              </div>
              <p className="text-sm tracking-tight text-gray-600 dark:text-gray-400">
                Click "Continue Working" to stay active, "Logout" to end your
                session, or wait to be automatically logged out.
              </p>
            </div>
          </div>

          <div className="flex flex-col w-full gap-2 sm:flex-row sm:gap-2">
            <Button
              onClick={signout}
              variant="outline"
              className="flex-1 w-full border-gray-300 hover:bg-gray-100 dark:border-gray-600 dark:hover:bg-gray-800"
              size="lg"
            >
              Logout
            </Button>
            <Button
              onClick={handleContinue}
              className="flex-1 w-full text-white bg-black hover:bg-gray-900"
              size="lg"
            >
              Continue Working
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
